// @/utils/timestampService.js
import { authManager } from '@/managers/AuthManager';
import { getApiBaseUrl } from './setProdOrDev'; // Import the new utility

class TimestampService {
  constructor() {
    this.endpointRetryCooldownMs = 30000;
    const apiBase = getApiBaseUrl(); // Get the base URL from the utility
    // Construct the URL dynamically
    this.prodUrl = `${apiBase}/timestamp`; 
    // If you uncommented a localUrl in setProdOrDev.js, you'd do:
    // this.localUrl = `${getApiBaseUrl()}/timestamp`;
    this.serverClientTimeDiff = 0;
    this.inFlightRequest = null;
    this.authRejectedForUserId = null;
    this.endpointUnavailableUntil = 0;
  }

  _getCurrentUserId() {
    return authManager.getCurrentUser()?.uid || null;
  }

  _resetEndpointStateIfNeeded() {
    const currentUserId = this._getCurrentUserId();
    if (this.authRejectedForUserId && this.authRejectedForUserId !== currentUserId) {
      this.authRejectedForUserId = null;
      this.endpointUnavailableUntil = 0;
    }
  }

  async _getAuthHeaders() {
    const user = authManager.getCurrentUser();
    if (!user?.getIdToken) {
      return {};
    }

    try {
      const token = await user.getIdToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
      return {};
    }
  }

  _fallbackDate() {
    return new Date(Date.now() + this.serverClientTimeDiff);
  }

  async _tryEndpoint(url, timeoutMs = 300, headers = {}) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const response = await fetch(url, { 
        signal: controller.signal,
        cache: 'no-cache',
        headers,
      });
      
      clearTimeout(timeoutId);

      if (response.status === 401 || response.status === 403) {
        const authError = new Error('auth-rejected');
        authError.code = 'AUTH_REJECTED';
        throw authError;
      }
      
      if (!response.ok) {
        throw new Error(`silent-fail`); // Don't log the actual HTTP status
      }
      
      const data = await response.json();
      if (!data || typeof data.timestamp !== 'number') {
        throw new Error('silent-fail');
      }
      
      return new Date(data.timestamp);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error?.code === 'AUTH_REJECTED') {
        throw error;
      }

      // Completely silent - these errors are expected during endpoint racing
      const silentError = new Error('silent-fail');
      silentError.code = 'SILENT_FAIL';
      throw silentError;
    }
  }

  async getTimestamp() {
    this._resetEndpointStateIfNeeded();

    const currentUserId = this._getCurrentUserId();
    if (Date.now() < this.endpointUnavailableUntil || (!currentUserId && this.authRejectedForUserId === null) || this.authRejectedForUserId === currentUserId) {
      return this._fallbackDate();
    }

    if (!this.inFlightRequest) {
      this.inFlightRequest = (async () => {
        const headers = await this._getAuthHeaders();

        try {
          const timestamp = await this._tryEndpoint(this.prodUrl, 300, headers);
          this.serverClientTimeDiff = timestamp.getTime() - Date.now();
          this.authRejectedForUserId = null;
          return timestamp;
        } catch (error) {
          if (error?.code === 'AUTH_REJECTED') {
            this.authRejectedForUserId = currentUserId;
            return this._fallbackDate();
          }

          try {
            const timestamp = await this._tryEndpoint(this.prodUrl, 1000, headers);
            this.serverClientTimeDiff = timestamp.getTime() - Date.now();
            return timestamp;
          } catch (retryError) {
            if (retryError?.code === 'AUTH_REJECTED') {
              this.authRejectedForUserId = currentUserId;
            } else {
              this.endpointUnavailableUntil = Date.now() + this.endpointRetryCooldownMs;
            }
            return this._fallbackDate();
          }
        }
      })().finally(() => {
        this.inFlightRequest = null;
      });
    }

    try {
      const timestamp = await this.inFlightRequest;
      return new Date(timestamp.getTime());
    } catch {
      try {
        return this._fallbackDate();
      } catch {
        return new Date();
      }
    }
  }
}

export const timestampService = new TimestampService();